import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { OMElementLibraryElementType } from '@deltek/specification-client/core/Models/projectElement'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Grid, Theme, Typography, makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { ElementItemDataNode } from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementTreeView'
import ElementsPanel from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementsPanel'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    container: { paddingBottom: theme.spacing(3.125) },
    partitionGrid: {
        width: 485,
        height: 375,
    },
    partitionGridAssembly: {
        width: 485,
        height: 375,
        backgroundColor: '#f2f2f2',
        marginTop: theme.spacing(0.25),
        '& .resize-triggers, .resize-triggers > div, .contract-trigger:before': {
            position: 'unset',
            height: 550,
            width: 400,
        },
    },
    outerText: {
        fontSize: pxToRem(14),
        paddingTop: theme.spacing(6),
        marginBottom: theme.spacing(2),
    },
    uploadBtnContainer: {
        paddingRight: theme.spacing(0.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    panel: {
        overflowX: 'hidden',
        transition: '100ms',
        zIndex: 1,
        '& *::-webkit-scrollbar': {
            width: 6,
            height: 20,
        },
        '& *::-webkit-scrollbar-thumb': {
            backgroundColor: '#464646',
            borderRadius: 100,
        },
        '& *::-webkit-scrollbar-track': {
            boxShadow: 'none',
        },
    },
    expandedPane: {
        width: 485,
        padding: theme.spacing(1),
    },
    fullWidthPaneContentContainer: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    elementsPane: {
        backgroundColor: '#F2F2F2',
    },
}))

interface ImportDuplicateVersionProps {
    setFamilyVersionsToImport: (familyVersions: ProjectElementNode[]) => void
    duplicateOfficeMasterImportFamilies: ProjectElementNode[]
}

const ImportDuplicateVersionDialogContent: React.FC<ImportDuplicateVersionProps> = ({
    setFamilyVersionsToImport,
    duplicateOfficeMasterImportFamilies,
}) => {
    const classes = useStyles()

    const [
        showElementsPanelFullLoadState,
        setShowElementsPanelFullLoadState,
    ] = React.useState(false)
    const [isFetchingMoreElements, setIsFetchingMoreElements] = React.useState(
        false
    )
    const [isNewDataLoading, setIsNewDataLoading] = React.useState<boolean>(
        false
    )
    const [isLoading, setIsLoading] = React.useState<boolean>(false)

    // contains the list of selected duplicate family versions from the elements panel
    const [
        selectedDuplicateFamilyVersionNodes,
        setSelectedDuplicateFamilyVersionNodes,
    ] = React.useState<ElementItemDataNode[]>([])

    React.useEffect(() => {
        // Update the list of families to import when the selection changes
        // ElementItemDataNode.elementId is the same as ProjectElementNode.id
        const familiesToImport = duplicateOfficeMasterImportFamilies.filter(
            (family) =>
                selectedDuplicateFamilyVersionNodes.some(
                    (selectedFamily) => selectedFamily.elementId === family.id
                )
        )
        setFamilyVersionsToImport(familiesToImport)
    }, [
        duplicateOfficeMasterImportFamilies,
        selectedDuplicateFamilyVersionNodes,
    ])

    const handleSelectDuplicateFamilyVersion = React.useCallback(
        (selectedElement: ElementItemDataNode) => {
            if (
                selectedElement.elementType ===
                OMElementLibraryElementType.PRODUCTFAMILYBUCKET
            ) {
                // container is not selectable..
                return
            }

            // handle selecting the version
            setSelectedDuplicateFamilyVersionNodes((prevSelectedVersions) => {
                // check if the selected element is already in the list
                const selfIndex = prevSelectedVersions.findIndex(
                    (selectedFamily) => selectedFamily.id === selectedElement.id
                )
                if (selfIndex !== -1) {
                    // if so we remove it, to toggle the selection
                    return prevSelectedVersions.filter(
                        (selectedFamily) =>
                            selectedFamily.id !== selectedElement.id
                    )
                }

                // check if the sibling (other version of the same family) of the selected element is already in the list
                const siblingIndex = prevSelectedVersions.findIndex(
                    (selectedFamily) =>
                        selectedFamily.parentNodeId ===
                        selectedElement.parentNodeId
                )
                if (siblingIndex !== -1) {
                    // if so we deselect the sibling and add the new selected element
                    return [
                        ...prevSelectedVersions.filter(
                            (selectedFamily) =>
                                selectedFamily.parentNodeId !==
                                selectedElement.parentNodeId
                        ),
                        selectedElement,
                    ]
                }

                // selected element/sibling was not found, so we just append it to the list
                return [...prevSelectedVersions, selectedElement]
            })
        },
        []
    )

    return (
        <GridContainer
            data-testid="importDuplicateVersionDialogContent"
            className={classes.container}
        >
            <GridItem className={classes.uploadBtnContainer}></GridItem>
            <Grid className={classes.partitionGrid}>
                <Typography className={classes.outerText}>
                    A previously imported family matches the families listed on
                    the right. <br /> You may overwrite this version or create a
                    new version instead.
                </Typography>
                <Typography className={classes.outerText}>
                    How do you want to proceed?
                </Typography>
            </Grid>
            <Grid
                id="elementsContainer"
                className={classes.partitionGridAssembly}
                data-testid="elementsContainer"
            >
                <GridContainer>
                    <GridItem
                        className={clsx(
                            classes.elementsPane,
                            classes.panel,
                            classes.expandedPane
                        )}
                    >
                        <div className={classes.fullWidthPaneContentContainer}>
                            <ElementsPanel
                                panelTitle="Duplicate Families"
                                calledFromImportPage={true}
                                calledFromImportDuplicateFamily={true}
                                elementFlatList={
                                    duplicateOfficeMasterImportFamilies
                                }
                                onLoadMoreClick={() => {}}
                                onLoadChildren={() => {}}
                                isNewDataLoading={isNewDataLoading}
                                isLoading={isLoading}
                                showFullLoadState={
                                    showElementsPanelFullLoadState
                                }
                                hasMoreElementsToFetch={isFetchingMoreElements}
                                handleSelectDuplicateFamilyVersion={
                                    handleSelectDuplicateFamilyVersion
                                }
                                selectedDuplicateFamilyVersionNodes={
                                    selectedDuplicateFamilyVersionNodes
                                }
                            />
                        </div>
                    </GridItem>
                </GridContainer>
            </Grid>
        </GridContainer>
    )
}

export default ImportDuplicateVersionDialogContent
